// src/pages/tabs/BillingTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useNavigate } from "react-router-dom"; // Import for navigation
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL;

const currency = (v) =>
  Number(v ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });

const liters = (v) =>
  Number(v ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

export default function BillingTab({ fromDate, toDate }) {
  const username = useMemo(
    () => localStorage.getItem("farmerUsername") || "F-B-3",
    []
  );
  const navigate = useNavigate(); // Initialize navigate function

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isMobile = useMediaQuery("(max-width:600px)");

  // 1) Fetch bill summary rows for the date range
  useEffect(() => {
    let cancel = false;
    const fetchBills = async () => {
      try {
        setLoading(true);
        setError("");
        const payload = {
          username,
          bill_fromdate: fromDate,
          bill_todate: toDate,
        };
        const { data } = await axios.post(`${API_BASE}/bill-details`, payload);
        if (cancel) return;

        const bills = Array.isArray(data?.bills) ? data.bills : [];
        // Map to concise table rows
        const mapped = bills.map((b) => ({
          bill_fromdate: b.bill_fromdate,
          bill_todate: b.bill_todate,
          totalLiters: Number(b.cowqty || 0) + Number(b.buffqty || 0),
          totalAmount: Number(b.cowamt || 0) + Number(b.buffamt || 0),
          totalDebit:
            Number(b.cowcommissionamt || 0) +
            Number(b.buffcommissionamt || 0) +
            Number(b.deposit_kapat || 0) +
            Number(b.dramt || 0),
          balance: Number(b.previousdeposit || 0),
          payable: Number(b.NetBill || 0),
        }));

        setRows(mapped);
      } catch (e) {
        console.error(e);
        setError("वरील कालावधीतील बिल रेकॉर्डस् उपलब्ध नाहीत.");
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    fetchBills();
    return () => {
      cancel = true;
    };
  }, [username, fromDate, toDate]);

  // 2) NEW: Function to handle navigation to the detailed Bill View page
  const handleViewBill = (bill) => {
    // Use absolute path to ensure navigation works from within tabs
    navigate(`/bill/${username}/${bill.bill_fromdate}/${bill.bill_todate}`);
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>🧾 बिलिंग माहिती</Typography>

      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">लोड होत आहे…</Typography>
        </Box>
      )}

      {error && (
        <Typography color="error" sx={{ mb: 1 }}>{error}</Typography>
      )}

      <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
        <Table stickyHeader size="small" aria-label="billing table">
          <TableHead>
            <TableRow>
              <TableCell>बिल कालावधी</TableCell>
              <TableCell align="right">एकूण लिटर्स</TableCell>
              <TableCell align="right">एकूण रक्कम (₹)</TableCell>
              <TableCell align="right">एकूण नावे (₹)</TableCell>
              <TableCell align="right">शिल्लक (₹)</TableCell>
              <TableCell align="right">देय (₹)</TableCell>
              <TableCell align="center">View Bill</TableCell> {/* Changed from "View Bill" */}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography align="center" color="text.secondary">
                    रेकॉर्ड आढळले नाहीत
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {rows.map((r) => {
              const key = `${r.bill_fromdate}_${r.bill_todate}`;
              return (
                <TableRow key={key} hover>
                  <TableCell>
                    {fmt(r.bill_fromdate)} - {fmt(r.bill_todate)}
                  </TableCell>
                  <TableCell align="right">{liters(r.totalLiters)}</TableCell>
                  <TableCell align="right">{currency(r.totalAmount)}</TableCell>
                  <TableCell align="right">{currency(r.totalDebit)}</TableCell>
                  <TableCell align="right">{currency(r.balance)}</TableCell>
                  <TableCell align="right">{currency(r.payable)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Detailed Bill">
                      <span>
                        <Button
                          size="small"
                          variant="outlined" // Changed from 'contained' to make it less prominent
                          // startIcon={<ReceiptIcon />} // Consider a different icon, like Receipt
                          onClick={() => handleViewBill(r)} // Use new navigation handler
                          sx={{
                            minWidth: isMobile ? 36 : 100, // Adjusted width
                            px: isMobile ? 1 : 2
                          }}
                        >
                          {isMobile ? "View" : "VIEW"} {/* Changed text */}
                        </Button>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* REMOVED: The Preview Dialog since it's moving to a new page */}

    </Box>
  );
}

function fmt(isoDate) {
  if (!isoDate) return "";
  const [y, m, d] = String(isoDate).split("-");
  return `${d}/${m}/${y}`;
}
